'use client'

import { noOp } from '@/utils/functions/no-op'
import { IChildrenProps } from '@/utils/typings/children-props'
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'

const KeybindingModuleContext = createContext<{
    active: boolean
    pingInactive: () => void
    setActive: () => void
}>({
    active: false,
    pingInactive: noOp,
    setActive: noOp,
})

export const useAllowKeybindings = () => useContext(KeybindingModuleContext).active

export const useKeybindingModuleContext = () => useContext(KeybindingModuleContext)

export const KeybindingModuleProvider: React.FC<IChildrenProps & { forceDisabled?: boolean }> = ({
    children,
    forceDisabled = false,
}) => {
    const [active, setActive] = useState(true)

    const { pingInactive: pingUpperModuleInactive, setActive: setUpperModuleActive } = useKeybindingModuleContext()

    useEffect(() => {
        let interval: NodeJS.Timeout | null = null

        if (active) {
            interval = setInterval(() => {
                pingUpperModuleInactive()
            }, 1000)
        } else {
            setUpperModuleActive()
        }

        return () => {
            if (interval) {
                clearInterval(interval)
            }
        }
    }, [active, pingUpperModuleInactive, setUpperModuleActive])

    const pingCounterRef = useRef(0)

    const pingInactive = useCallback(() => {
        pingCounterRef.current++

        const currentCounter = pingCounterRef.current

        if (active) setActive(false)

        const timeout = setTimeout(() => {
            // If no further pings have been made, set the module active
            // This is to prevent the module from being stuck in an inactive state
            // Scenario: Keybinding module below is not correctly unmounted, so the upper module is never set active
            // This will ensure that the module is set active after at most 2 seconds
            if (currentCounter >= pingCounterRef.current) {
                setActive(true)
            }

            clearTimeout(timeout)
        }, 2000)
    }, [active])

    const value = useMemo(
        () =>
            forceDisabled
                ? { active: false, setActive: noOp, pingInactive: noOp }
                : { active, setActive: () => setActive(true), pingInactive },
        [active, pingInactive, forceDisabled]
    )

    return <KeybindingModuleContext.Provider value={value}>{children}</KeybindingModuleContext.Provider>
}

export const useWithUpperKeyboardShortcutsRestoration = (fn: (args: any) => any) => {
    const { setActive: setUpperKeybindingModuleActive } = useKeybindingModuleContext()

    return useCallback(
        (args: unknown) => {
            fn(args)
            setUpperKeybindingModuleActive()
        },
        [fn, setUpperKeybindingModuleActive]
    )
}
